import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    InputAdornment,
    IconButton,
    Popover,
    Checkbox,
    Button,
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    SwapHoriz as SwapHorizIcon,
    CardGiftcard as CardGiftcardIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axios from '../config/axiosConfig';
import TitleAndInfo from './TitleAndInfo';
import { findBioguideId } from '../utils/congressMembersMapping';
import ProfileImage from './ProfileImage';

const PAGE_SIZE = 10;
const DEBOUNCE_MS = 400;

const TYPE_OPTIONS = [
    { key: 'Purchase', label: 'Purchase', icon: <TrendingUpIcon sx={{ fontSize: '0.9rem' }} />, color: 'positive' },
    { key: 'Sale', label: 'Sale', icon: <TrendingDownIcon sx={{ fontSize: '0.9rem' }} />, color: 'negative' },
    { key: 'Exchange', label: 'Exchange', icon: <SwapHorizIcon sx={{ fontSize: '0.9rem' }} />, color: 'text' },
    { key: 'Receive', label: 'Receive', icon: <CardGiftcardIcon sx={{ fontSize: '0.9rem' }} />, color: 'text' },
];

const AMOUNT_BUCKETS = [
    '$1,001 - $15,000',
    '$15,001 - $50,000',
    '$50,001 - $100,000',
    '$100,001 - $250,000',
    '$250,001 - $500,000',
    '$500,001 - $1,000,000',
    '$1,000,001 - $5,000,000',
    '$5,000,001 - $25,000,000',
    '$25,000,001 - $50,000,000',
    'Over $50,000,000',
];

// --- Filter Components ---

const TypeFilter = ({ selectedTypes, setSelectedTypes, theme }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleToggle = (key) =>
        setSelectedTypes((prev) =>
            prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
        );

    const getColor = (colorKey) => {
        if (colorKey === 'positive') return theme.palette.positive?.main || theme.palette.success.main;
        if (colorKey === 'negative') return theme.palette.negative?.main || theme.palette.error.main;
        return theme.palette.text.secondary;
    };

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    borderRadius: 2, textTransform: 'none', fontSize: '0.875rem',
                    minWidth: 140, justifyContent: 'space-between',
                    color: 'text.primary', borderColor: 'divider', height: 40, px: 1.5,
                }}
            >
                {selectedTypes.length > 0 ? `Type (${selectedTypes.length})` : 'Type'}
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { mt: 0.5, borderRadius: 2, width: 200 } }}
            >
                {TYPE_OPTIONS.map((opt) => {
                    const color = getColor(opt.color);
                    return (
                        <MenuItem
                            key={opt.key}
                            onClick={() => handleToggle(opt.key)}
                            dense
                            sx={{ py: 0.25, px: 1, minHeight: 32 }}
                        >
                            <Checkbox
                                checked={selectedTypes.includes(opt.key)}
                                size="small"
                                sx={{ p: 0.25, mr: 0.75 }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ color, display: 'flex' }}>{opt.icon}</Box>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color, fontWeight: 600 }}>
                                    {opt.label}
                                </Typography>
                            </Box>
                        </MenuItem>
                    );
                })}
                {selectedTypes.length > 0 && (
                    <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => setSelectedTypes([])} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                            Clear all
                        </Button>
                    </Box>
                )}
            </Popover>
        </>
    );
};

const AmountFilter = ({ selectedAmounts, setSelectedAmounts }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleToggle = (bucket) =>
        setSelectedAmounts((prev) =>
            prev.includes(bucket) ? prev.filter((b) => b !== bucket) : [...prev, bucket]
        );

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    borderRadius: 2, textTransform: 'none', fontSize: '0.875rem',
                    minWidth: 140, justifyContent: 'space-between',
                    color: 'text.primary', borderColor: 'divider', height: 40, px: 1.5,
                }}
            >
                {selectedAmounts.length > 0 ? `Amount (${selectedAmounts.length})` : 'Amount'}
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { mt: 0.5, borderRadius: 2, width: 280, maxHeight: 350 } }}
            >
                {AMOUNT_BUCKETS.map((bucket) => (
                    <MenuItem
                        key={bucket}
                        onClick={() => handleToggle(bucket)}
                        dense
                        sx={{ py: 0.25, px: 1, minHeight: 32 }}
                    >
                        <Checkbox
                            checked={selectedAmounts.includes(bucket)}
                            size="small"
                            sx={{ p: 0.25, mr: 0.75 }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {bucket}
                        </Typography>
                    </MenuItem>
                ))}
                {selectedAmounts.length > 0 && (
                    <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => setSelectedAmounts([])} sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                            Clear all
                        </Button>
                    </Box>
                )}
            </Popover>
        </>
    );
};

// --- Main Component ---

const CongressTracker = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const baseUrl = window.location.origin;

    // Pagination state
    const [page, setPage] = useState(0);
    const [totalRows, setTotalRows] = useState(0);

    // Filter state
    const [selectedParty, setSelectedParty] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedAmounts, setSelectedAmounts] = useState([]);

    // Cache state - key is a composite of all filters
    const [cachedData, setCachedData] = useState({});
    const cachedDataRef = useRef(cachedData);
    cachedDataRef.current = cachedData;

    const [initialLoadDone, setInitialLoadDone] = useState(false);

    const debounceTimer = useRef(null);

    const titleTop = "Congress Trading Activity";
    const title = "";
    const info = "Track trading activities of members of Congress, including both Senate and House representatives. View detailed information about stock purchases, sales, and transaction amounts, sorted by date to monitor the most recent trading patterns.";

    // Build a cache key from all active filters
    const getCacheKey = useCallback((party, search, types, amounts) => {
        return `${party || 'all'}|${search}|${types.join(',')}|${amounts.join(',')}`;
    }, []);

    // Debounce search input
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, DEBOUNCE_MS);
        return () => clearTimeout(debounceTimer.current);
    }, [searchQuery]);

    // Reset page when filters change (except search, which is handled by debounce)
    useEffect(() => {
        setPage(0);
    }, [selectedParty, selectedTypes, selectedAmounts]);

    const fetchTradesData = useCallback(async (page, party, search, types, amounts) => {
        const cacheKey = getCacheKey(party, search, types, amounts);
        const pageCache = cachedDataRef.current[cacheKey] || {};
        if (pageCache[page] !== undefined) {
            setTotalRows(pageCache._total ?? 0);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const params = {
                page: page + 1,
                limit: PAGE_SIZE,
                party: party || undefined,
                search: search || undefined,
                type: types.length > 0 ? types.join(',') : undefined,
                amount: amounts.length > 0 ? amounts.join('|') : undefined,
            };

            const response = await axios.get('/api/congress/trading', { params });
            const { data: responseData, total } = response.data;

            const transformedData = responseData.map(trade => ({
                id: trade._id,
                personName: trade.name,
                personFirstLetter: trade.name.charAt(0),
                party: trade.party,
                companySymbol: trade.symbol,
                companyName: trade.assetDescription,
                date: new Date(trade.transactionDate),
                transactionDate: trade.transactionDate,
                filedDate: trade.disclosureDate ? new Date(trade.disclosureDate) : null,
                amount: trade.amount,
                type: trade.transactionType
            }));

            setCachedData(prev => ({
                ...prev,
                [cacheKey]: {
                    ...(prev[cacheKey] || {}),
                    [page]: transformedData,
                    _total: total
                }
            }));

            setTotalRows(total);
            setInitialLoadDone(true);
            setError(null);
        } catch (error) {
            console.error('Error fetching trades data:', error);
            setError(error.message || 'Failed to fetch trades data');
        } finally {
            setLoading(false);
        }
    }, [getCacheKey]);

    useEffect(() => {
        fetchTradesData(page, selectedParty, debouncedSearch, selectedTypes, selectedAmounts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, selectedParty, debouncedSearch, selectedTypes, selectedAmounts]);

    const handlePartyChange = (event) => {
        setSelectedParty(event.target.value);
    };

    const hasActiveFilters = searchQuery.trim() !== '' || selectedParty !== '' || selectedTypes.length > 0 || selectedAmounts.length > 0;

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedParty('');
        setSelectedTypes([]);
        setSelectedAmounts([]);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setDebouncedSearch('');
        setPage(0);
    };

    // Get current data from cache
    const cacheKey = getCacheKey(selectedParty, debouncedSearch, selectedTypes, selectedAmounts);
    const currentData = (cachedData[cacheKey] || {})[page] || [];

    const columns = [
        {
            field: 'person',
            headerName: 'Person',
            minWidth: 250,
            flex: 1,
            renderCell: (params) => {
                const bioguideId = findBioguideId(params.row.personName);

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ProfileImage
                            name={params.row.personName}
                            imageUrls={[
                                bioguideId
                                    ? `/images/congress/${bioguideId}.jpg`
                                    : null
                            ]}
                            size={32}
                            sx={{
                                flexShrink: 0,
                                width: 32,
                                height: 32
                            }}
                        />
                        <Box>
                            <RouterLink
                                to={`/congress-member/${encodeURIComponent(params.row.personName)}`}
                                style={{
                                    color: theme.palette.primary.main,
                                    textDecoration: 'none',
                                    fontSize: '1rem',
                                }}
                            >
                                {params.row.personName}
                            </RouterLink>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                }}
                            >
                                {params.row.party}
                            </Typography>
                        </Box>
                    </Box>
                );
            }
        },
        {
            field: 'symbol',
            headerName: 'Company',
            minWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <Box>
                    <RouterLink
                        to={`${baseUrl}/asset/${params.row.companySymbol}`}
                        style={{
                            color: theme.palette.primary.main,
                            textDecoration: 'none',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                        }}
                    >
                        {params.row.companySymbol}
                    </RouterLink>
                    <Typography
                        variant="body2"
                        title={params.row.companyName}
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                            overflow: 'hidden',
                            textOverflow: { md: 'ellipsis' },
                            maxWidth: '100%',
                            lineHeight: 1.3,
                        }}
                    >
                        {params.row.companyName}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'date',
            headerName: 'Traded',
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) => {
                return new Date(params.value).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            }
        },
        {
            field: 'filedDate',
            headerName: 'Filed',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                if (!params.value) return <Typography variant="body2" color="text.secondary">-</Typography>;
                const traded = params.row.date;
                const filed = params.value;
                const daysDiff = Math.round((filed - traded) / (1000 * 60 * 60 * 24));
                return (
                    <Box>
                        <Typography variant="body2">
                            {filed.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            })}
                        </Typography>
                        {daysDiff > 0 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: daysDiff > 45 ? 'error.main' : daysDiff > 30 ? 'warning.main' : 'text.secondary',
                                    fontWeight: daysDiff > 30 ? 600 : 400,
                                }}
                            >
                                {daysDiff}d later
                            </Typography>
                        )}
                    </Box>
                );
            }
        },
        {
            field: 'amount',
            headerName: 'Amount',
            minWidth: 200,
            flex: 1
        },
        {
            field: 'type',
            headerName: 'Type',
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <Typography
                    sx={{
                        color: params.value.toLowerCase() === 'purchase' ||
                            params.value.toLowerCase() === 'buy' ?
                            theme.palette.positive.main :
                            theme.palette.negative.main
                    }}
                >
                    {params.value.toLowerCase() === 'purchase' ? 'Bought' :
                        params.value.toLowerCase() === 'sale' ? 'Sold' :
                            params.value}
                </Typography>
            )
        }
    ];

    // Track if this is the initial load
    const isInitialLoad = !initialLoadDone && loading;

    if (isInitialLoad) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {error}
            </Alert>
        );
    }

    return (
        <Container maxWidth="xl">
            <TitleAndInfo titleTop={titleTop} title={title} info={info} />

            {/* Filters Row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                {/* Search */}
                <TextField
                    size="small"
                    placeholder="Search ticker or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            </InputAdornment>
                        ),
                        ...(searchQuery && {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }),
                    }}
                    sx={{
                        minWidth: 220,
                        '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' },
                    }}
                />

                {/* Party */}
                <FormControl sx={{ minWidth: 160 }} size="small">
                    <InputLabel>Party</InputLabel>
                    <Select
                        value={selectedParty}
                        onChange={handlePartyChange}
                        label="Party"
                        sx={{ borderRadius: 2 }}
                    >
                        <MenuItem value="">All Parties</MenuItem>
                        <MenuItem value="Democratic">Democratic</MenuItem>
                        <MenuItem value="Republican">Republican</MenuItem>
                        <MenuItem value="Independent">Independent</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </Select>
                </FormControl>

                {/* Type */}
                <TypeFilter
                    selectedTypes={selectedTypes}
                    setSelectedTypes={setSelectedTypes}
                    theme={theme}
                />

                {/* Amount */}
                <AmountFilter
                    selectedAmounts={selectedAmounts}
                    setSelectedAmounts={setSelectedAmounts}
                />

                {/* Clear All */}
                {hasActiveFilters && (
                    <Button
                        size="small"
                        onClick={clearFilters}
                        startIcon={<CloseIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        Clear
                    </Button>
                )}
            </Box>

            <Box sx={{ height: 650, width: '100%' }}>
                <DataGrid
                    rows={currentData}
                    columns={columns}
                    paginationMode="server"
                    rowCount={totalRows}
                    pageSize={PAGE_SIZE}
                    paginationModel={{
                        page,
                        pageSize: PAGE_SIZE,
                    }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                    }}
                    loading={loading}
                    disableColumnMenu
                    hideFooterRowCount
                    hideFooterSelectedRowCount
                />
            </Box>
        </Container>
    );
};

export default CongressTracker;